import type React from "react";
import { useEffect, useState } from "react";
import Icon from "../../../lib/components/custom/Icon";
import { Image } from "../../../lib/components/custom/Image";
import { Button } from "../../../lib/components/ui/button";
import { ScrollArea } from "../../../lib/components/ui/scroll-area";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../../lib/components/ui/tabs";
import { useApi } from "../../../lib/hooks/api/use-api";

interface ImageSelectorProps {
	category: string;
	onSelect: (value: string | File) => void;
	selectedPreview?: string | null;
}

export function ImageSelector({
	category,
	onSelect,
	selectedPreview,
}: ImageSelectorProps) {
	const api = useApi();
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [selectedType, setSelectedType] = useState<
		"category" | "upload" | null
	>(null);

	// Fetch category images
	const { data: categoryImages, isLoading: isLoadingImages } =
		api.menu.getCategoryImages({ category });

	// Fetch recent images for this category (we don't have entityId for new items, so passing 0 or omitting might be needed)
	// For now we will just show category images and upload option
	// If we wanted recent images, we'd need to know if we are editing or creating, but generic recent images might not be relevant without context.

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Create preview URL
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		onSelect(file);
		setSelectedType("upload");
	};

	const handleCategoryImageSelect = (img: string) => {
		onSelect(img);
		setSelectedType("category");
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}
	};

	// Cleanup preview URL when component unmounts
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	// Initialize state based on existing preview
	useEffect(() => {
		if (selectedPreview && !previewUrl && !selectedType) {
			// If we have a preview but no local state, it might be a category image or existing url
			// We don't strictly need to set selectedType here as it's just for UI highlighting
		}
	}, [selectedPreview]);

	return (
		<div className="border rounded-xl overflow-hidden bg-card">
			<Tabs defaultValue="category" className="flex flex-col">
				<TabsList className="p-0 w-full justify-start rounded-none border-b bg-muted/30">
					<TabsTrigger
						value="category"
						className="flex-1 rounded-none data-[state=active]:bg-background border-r"
					>
						Category Images
					</TabsTrigger>
					<TabsTrigger
						value="upload"
						className="flex-1 rounded-none data-[state=active]:bg-background"
					>
						Upload New
					</TabsTrigger>
				</TabsList>

				<TabsContent value="category" className="p-0 m-0">
					<ScrollArea className="h-[300px]">
						<div className="p-4">
							{isLoadingImages ? (
								<div className="flex items-center justify-center h-40 text-muted-foreground">
									<Icon name="Loader" className="w-5 h-5 animate-spin mr-2" />
									Loading images...
								</div>
							) : categoryImages && categoryImages.length > 0 ? (
								<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
									{categoryImages.map((img: string, i: number) => (
										<div key={i} className="relative group aspect-square">
											<Image
												src={img}
												alt={`Category option ${i}`}
												className="w-full h-full object-cover cursor-pointer rounded-lg hover:opacity-80 transition-opacity border-2 border-transparent hover:border-primary/50"
												onClick={() => handleCategoryImageSelect(img)}
											/>

											{selectedPreview === img && (
												<div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px] rounded-lg pointer-events-none">
													<div className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-lg">
														<Icon name="Check" className="w-3 h-3" />
														Selected
													</div>
												</div>
											)}
										</div>
									))}
								</div>
							) : (
								<div className="text-center py-10 text-muted-foreground">
									No category images found.
								</div>
							)}
						</div>
					</ScrollArea>
				</TabsContent>

				<TabsContent value="upload" className="p-0 m-0">
					<ScrollArea className="h-[300px]">
						<div className="p-4">
							{previewUrl ? (
								<div className="space-y-4">
									<div className="relative max-w-[200px] mx-auto aspect-square">
										<img
											src={previewUrl}
											alt="Preview"
											className="w-full h-full object-cover rounded-lg border-2 border-primary/20"
										/>
										<div className="absolute top-2 right-2">
											<Button
												variant="destructive"
												size="icon"
												className="h-8 w-8 rounded-full shadow-md"
												onClick={(e) => {
													e.stopPropagation();
													setPreviewUrl(null);
													const fileInput = document.getElementById(
														"image-upload-input",
													) as HTMLInputElement;
													if (fileInput) fileInput.value = "";
													onSelect(""); // Clear selection
												}}
											>
												<Icon name="X" className="w-4 h-4" />
											</Button>
										</div>
									</div>
									<div className="text-center">
										<p className="text-sm text-muted-foreground">
											This image will be uploaded when you create the item.
										</p>
									</div>
								</div>
							) : (
								<div className="flex flex-col items-center justify-center h-[200px] border-2 border-dashed border-muted-foreground/20 rounded-xl m-4 hover:bg-muted/10 transition-colors">
									<input
										id="image-upload-input"
										type="file"
										accept="image/*"
										className="hidden"
										onChange={handleFileSelect}
									/>
									<Button
										variant="outline"
										onClick={() =>
											document.getElementById("image-upload-input")?.click()
										}
										className="mb-2"
									>
										<Icon name="Upload" className="w-4 h-4 mr-2" />
										Select Image
									</Button>
									<p className="text-xs text-muted-foreground">
										JPG, PNG or WebP up to 5MB
									</p>
								</div>
							)}
						</div>
					</ScrollArea>
				</TabsContent>
			</Tabs>
		</div>
	);
}
